/**
 * Static content for the MoveTwo landing page.
 * Shared constants also consumed by EmptyState.
 */

export const EXTENSION_URL =
  'https://chromewebstore.google.com/detail/movetwo/PLACEHOLDER';

export const PROPERTY_SITES = [
  { name: 'Rightmove', url: 'https://www.rightmove.co.uk' },
  { name: 'Zoopla', url: 'https://www.zoopla.co.uk' },
  { name: 'OpenRent', url: 'https://www.openrent.com' },
];

export const AGENT_SITES = [
  'Dexters',
  'Foxtons',
  'Winkworth',
  'Belvoir',
  'Benhams',
];

export interface SupportedCountry {
  code: string;
  name: string;
  flag: string;
  sites: { name: string; url: string }[];
}

export const SUPPORTED_COUNTRIES: SupportedCountry[] = [
  {
    code: 'GB', name: 'United Kingdom', flag: '\uD83C\uDDEC\uD83C\uDDE7',
    sites: [
      { name: 'Rightmove', url: 'https://rightmove.co.uk' },
      { name: 'Zoopla', url: 'https://zoopla.co.uk' },
      { name: 'OpenRent', url: 'https://openrent.co.uk' },
      { name: 'OnTheMarket', url: 'https://onthemarket.com' },
      { name: 'Dexters', url: 'https://dexters.co.uk' },
      { name: 'Foxtons', url: 'https://foxtons.co.uk' },
      { name: 'Winkworth', url: 'https://winkworth.co.uk' },
      { name: 'Belvoir', url: 'https://belvoir.co.uk' },
      { name: 'Benhams', url: 'https://benhams.com' },
    ],
  },
  {
    code: 'US', name: 'United States', flag: '\uD83C\uDDFA\uD83C\uDDF8',
    sites: [
      { name: 'Zillow', url: 'https://zillow.com' },
      { name: 'Realtor.com', url: 'https://realtor.com' },
      { name: 'Redfin', url: 'https://redfin.com' },
      { name: 'Apartments.com', url: 'https://apartments.com' },
      { name: 'Trulia', url: 'https://trulia.com' },
    ],
  },
  {
    code: 'FR', name: 'France', flag: '\uD83C\uDDEB\uD83C\uDDF7',
    sites: [
      { name: 'SeLoger', url: 'https://seloger.com' },
      { name: 'LeBonCoin', url: 'https://leboncoin.fr' },
      { name: 'PAP', url: 'https://pap.fr' },
      { name: 'Logic-Immo', url: 'https://logic-immo.com' },
      { name: 'BienIci', url: 'https://bienici.com' },
    ],
  },
  {
    code: 'DE', name: 'Germany', flag: '\uD83C\uDDE9\uD83C\uDDEA',
    sites: [
      { name: 'ImmobilienScout24', url: 'https://immobilienscout24.de' },
      { name: 'Immonet', url: 'https://immonet.de' },
      { name: 'Immowelt', url: 'https://immowelt.de' },
      { name: 'WG-Gesucht', url: 'https://wg-gesucht.de' },
      { name: 'Kleinanzeigen', url: 'https://kleinanzeigen.de' },
    ],
  },
  {
    code: 'SA', name: 'Saudi Arabia', flag: '\uD83C\uDDF8\uD83C\uDDE6',
    sites: [
      { name: 'Aqar', url: 'https://aqar.fm' },
      { name: 'Bayut.sa', url: 'https://bayut.sa' },
      { name: 'Wasalt', url: 'https://wasalt.com' },
      { name: 'Haraj', url: 'https://haraj.com.sa' },
      { name: 'Ejar', url: 'https://ejar.sa' },
    ],
  },
  {
    code: 'AE', name: 'UAE', flag: '\uD83C\uDDE6\uD83C\uDDEA',
    sites: [
      { name: 'Bayut', url: 'https://bayut.com' },
      { name: 'PropertyFinder', url: 'https://propertyfinder.ae' },
      { name: 'Dubizzle', url: 'https://dubizzle.com' },
      { name: 'Houza', url: 'https://houza.com' },
    ],
  },
];

export interface Feature {
  icon: string;
  title: string;
  description: string;
}

export const FEATURES: Feature[] = [
  {
    icon: 'Clock',
    title: 'Travel Times',
    description:
      'See commute times to your workplaces overlaid directly on Rightmove, Zoopla, and OpenRent listings.',
  },
  {
    icon: 'Users',
    title: 'Shared Lists',
    description:
      'Save properties and your partner sees them instantly. No more forwarding links back and forth.',
  },
  {
    icon: 'Star',
    title: 'Ratings',
    description:
      'Rate every property independently, then compare scores side by side to find your best match.',
  },
  {
    icon: 'MessageCircle',
    title: 'Comments & Notes',
    description:
      'Discuss properties with your partner and attach photos from viewings — all in one place.',
  },
  {
    icon: 'Globe',
    title: 'International Support',
    description:
      'Works across 34 property websites in 6 countries: UK, USA, France, Germany, Saudi Arabia, and UAE.',
  },
  {
    icon: 'Heart',
    title: 'Status Tracking',
    description:
      'Track every property through your pipeline: interested, viewing, applied, offered, or rejected.',
  },
];

export interface Step {
  number: number;
  title: string;
  description: string;
}

export const STEPS: Step[] = [
  {
    number: 1,
    title: 'Sign Up & Invite',
    description:
      'Create your account and share an invite code with your partner or housemate.',
  },
  {
    number: 2,
    title: 'Save & Collaborate',
    description:
      'Browse property sites with the Chrome extension. Save listings to your shared list in one click.',
  },
  {
    number: 3,
    title: 'Rate & Decide',
    description:
      'Rate, comment, and compare properties together until you find the one.',
  },
];

export const PRICING_PLAN = {
  name: 'MoveTwo Pro',
  monthly: '\u00A36.99',
  quarterly: '\u00A314.97',
  quarterlyMonthly: '\u00A34.99',
  annual: '\u00A341.99',
  annualMonthly: '\u00A33.50',
  savingsPercent: 50,
  quarterlySavingsPercent: 29,
  trialDays: 10,
  features: [
    'Unlimited saved locations',
    'Unlimited properties',
    'Photos, ratings & comments',
    'Full status pipeline',
    'Travel time calculations',
    'Real-time partner sync',
  ],
  cta: 'Start Free Trial',
};

export interface FaqItem {
  question: string;
  answer: string;
}

export const FAQ_ITEMS: FaqItem[] = [
  {
    question: 'How does MoveTwo work?',
    answer:
      'Install the Chrome extension and create an account. When you browse property sites like Rightmove or Zoopla, a small widget lets you save listings to a shared list. Your partner can see everything in real time on the web dashboard.',
  },
  {
    question: 'Is there a free trial?',
    answer:
      'Yes! Every account starts with a 10-day free trial of MoveTwo Pro \u2014 no credit card required. You get full access to all features during the trial.',
  },
  {
    question: 'Can I cancel anytime?',
    answer:
      'Absolutely. You can cancel your subscription at any time from your account settings. Your data is kept so you can pick up where you left off if you resubscribe.',
  },
  {
    question: 'Which property sites are supported?',
    answer:
      'MoveTwo supports 34 property sites across 6 countries. In the UK: Rightmove, Zoopla, OpenRent, and more. In the US: Zillow, Realtor.com, Redfin. Plus sites in France, Germany, Saudi Arabia, and UAE.',
  },
  {
    question: 'Do both people need the extension?',
    answer:
      'No. Only one person needs the Chrome extension to save properties. Your partner can view, rate, and comment on everything from the web dashboard — no extension required.',
  },
  {
    question: 'Can I use it for buying, not renting?',
    answer:
      'Absolutely. MoveTwo works with any property listing on supported sites, whether you\'re renting or buying.',
  },
  {
    question: 'Is my data private?',
    answer:
      'Yes. Your property lists and comments are only visible to members of your search. We don\'t sell data or show ads.',
  },
  {
    question: 'What happens when I find a flat?',
    answer:
      'Mark it as "offered" or "accepted" in your pipeline. You can archive the search and start a new one anytime.',
  },
  {
    question: 'Can more than 2 people use it?',
    answer:
      'Currently, each search supports 2 members — perfect for couples or a pair of housemates. Support for larger groups is on our roadmap.',
  },
];

export const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'How it Works', href: '#how-it-works' },
  { label: 'Sites', href: '#supported-sites' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
];
